import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { WorkflowAutomatedActivitiesService } from './workflow-automated-activities.service';
import type {
  WorkflowAutomatedActivitiesRead,
  WorkflowAutomatedActivityRun,
} from './workflow-automated-activities.models';
@Component({
  selector: 'phms-workflow-automated-activities',
  standalone: true,
  imports: [
    EzStandardStateComponent,
    CommonModule,
    FormsModule,
    RouterLink,
    UiButtonDirective,
    UiControlDirective,
  ],
  template: `<section
    class="activity-history"
    aria-labelledby="automated-activity-history-title"
    [attr.aria-busy]="loading || busy"
  >
    <header>
      <h2 id="automated-activity-history-title">Automated execution</h2>
      <button
        uiButton
        type="button"
        (click)="refresh()"
        [disabled]="busy || interactionBlocked || loading"
      >
        {{
          data
            ? 'Refresh automated execution'
            : 'View automated execution and history'
        }}
      </button>
    </header>
    <p *ngIf="!data && !loading && !error && !readErrorKind">
      Read recorded automated work, its results and recovery availability. This
      does not start or execute an activity.
    </p>
    <ui-standard-state
      *ngIf="loading || readErrorKind"
      [kind]="loading ? 'loading' : readErrorKind!"
      [message]="loading ? 'Loading automated execution…' : error"
    />
    <p *ngIf="error && !readErrorKind" role="alert">{{ error }}</p>
    <p *ngIf="notice" role="status">{{ notice }}</p>
    <ng-container *ngIf="!loading && !readErrorKind && data as state"
      ><p>
        System worker
        {{ state.workerConfigured ? 'configured' : 'not configured' }} ·
        dispatch
        {{ state.dispatchEnabled ? 'enabled' : 'disabled by installation' }}.
      </p>
      <p *ngIf="!state.workerConfigured || !state.dispatchEnabled">
        Installed activity support does not enable execution. This page cannot
        grant service authority or change worker configuration.
      </p>
      <ui-standard-state
        *ngIf="!rows.length"
        kind="empty"
        message="No automated activity is recorded for this selection."
      />
      <label *ngIf="rows.length"
        >Recorded invocation<select
          uiControl
          [ngModel]="selected?.invocationId || ''"
          (ngModelChange)="select($event)"
          [ngModelOptions]="standalone"
          [disabled]="busy || interactionBlocked"
        >
          <option value="">Choose recorded work</option>
          <option *ngFor="let run of rows" [value]="run.invocationId">
            {{ run.stepCode }} · occurrence {{ run.occurrenceId }} ·
            {{ statusLabel(run.status) }}
          </option>
        </select></label
      >
      <p *ngIf="state.historyTruncated">
        History is bounded; older execution records remain retained by the
        server.
      </p>
      <section *ngIf="selected as run">
        <h3>{{ run.stepCode }} · {{ statusLabel(run.status) }}</h3>
        <p>
          {{
            run.isCurrent
              ? 'Current recorded occurrence'
              : 'Retained historical work'
          }}
          · {{ run.attempts }} execution attempt{{
            run.attempts === 1 ? '' : 's'
          }}.
        </p>
        <p *ngIf="run.outcomeCode">
          Business outcome: <strong>{{ run.outcomeCode }}</strong>
        </p>
        <p *ngIf="run.issueCode" class="activity-issue">
          Technical or blocking issue: {{ run.issueCode }}. Required work
          remains blocked until the server records permitted recovery or
          completion.
        </p>
        <p *ngIf="run.reason">Recorded reason: {{ run.reason }}</p>
        <p *ngIf="run.supersedesInvocationId">
          Replaces invocation {{ run.supersedesInvocationId }}. Its earlier
          result and attempts remain retained.
        </p>
        <p *ngIf="stale" role="status">
          This source changed. Refresh its current workflow and occurrence state
          before retrying. Your reason is kept.
        </p>
        <h4>Recorded result</h4>
        <dl *ngIf="resultFields.length; else noResult">
          <div *ngFor="let field of resultFields">
            <dt>{{ field.name }}</dt>
            <dd>{{ field.value === null ? 'No value' : field.value }}</dd>
          </div>
        </dl>
        <ng-template #noResult
          ><p>
            No readable result has been recorded. Missing or protected values
            are not inferred.
          </p></ng-template
        >
        <details>
          <summary>Exact execution identity</summary>
          <p>
            Occurrence {{ run.occurrenceId }} · node {{ run.nodeId }} ·
            invocation {{ run.invocationId }}
          </p>
          <p>
            Command {{ run.command.key.provider }} /
            {{ run.command.key.code }} v{{ run.command.key.version }} ·
            {{ run.command.contractHash }}
          </p>
          <p>
            Workflow source {{ run.workflowETag }} · occurrence source
            {{ run.occurrenceETag }}
          </p>
        </details>
        <a
          *ngIf="run.eventId && run.consumerId"
          [routerLink]="['/platform-admin/operations']"
          [queryParams]="{ eventId: run.eventId, consumerId: run.consumerId }"
          >Inspect delivery attempts and retry in Jobs &amp; outbox</a
        >
        <form
          *ngIf="run.canCancel || run.canRestart"
          (ngSubmit)="$event.preventDefault()"
        >
          <label
            >Recovery reason<textarea
              uiControl
              rows="2"
              maxlength="1000"
              [ngModel]="reason"
              (ngModelChange)="reason = $event"
              name="activityRecoveryReason"
              [disabled]="busy || interactionBlocked"
            ></textarea>
          </label>
          <div class="activity-actions">
            <button
              *ngIf="run.canCancel"
              uiButton
              type="button"
              (click)="control('cancel')"
              [disabled]="busy || interactionBlocked || stale || !reason.trim()"
            >
              Cancel required activity</button
            ><button
              *ngIf="run.canRestart"
              uiButton
              type="button"
              (click)="control('restart')"
              [disabled]="busy || interactionBlocked || stale || !reason.trim()"
            >
              Create replacement activity
            </button>
          </div>
          <p>
            Cancellation does not undo committed effects. A replacement is new
            reasoned work; its eligibility is rechecked by the server. A
            delivery retry retains the same invocation and belongs in Jobs &amp;
            outbox.
          </p>
        </form>
      </section>
    </ng-container>
  </section>`,
  styles: [
    `
      .activity-history {
        display: grid;
        gap: 0.6rem;
        border-top: 1px solid var(--custom-border-color);
        padding-top: 0.75rem;
        min-width: 0;
      }
      header,
      .activity-actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
      }
      h2,
      h3,
      h4,
      p {
        margin: 0 0 0.35rem;
      }
      label {
        display: grid;
        gap: 0.25rem;
      }
      select,
      textarea {
        width: 100%;
      }
      dl {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
        gap: 0.5rem;
      }
      dd {
        margin: 0.15rem 0;
        overflow-wrap: anywhere;
      }
      dt {
        color: var(--custom-text-muted-color);
      }
      [role='alert'],
      .activity-issue {
        color: var(--custom-text-color-error);
      }
      details {
        overflow-wrap: anywhere;
      }
    `,
  ],
})
export class WorkflowAutomatedActivitiesComponent
  implements OnChanges, OnDestroy
{
  @Input({ required: true }) workflowId = 0;
  @Input() occurrenceId: number | null = null;
  @Input() autoLoad = false;
  @Input() interactionBlocked = false;
  @Output() busyChange = new EventEmitter<boolean>();
  @Output() pendingInputChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<void>();
  private readonly api = inject(WorkflowAutomatedActivitiesService);
  data: WorkflowAutomatedActivitiesRead | null = null;
  rows: WorkflowAutomatedActivityRun[] = [];
  selected: WorkflowAutomatedActivityRun | null = null;
  resultFields: { name: string; value: string | number | boolean | null }[] =
    [];
  loading = false;
  busy = false;
  stale = false;
  error = '';
  readErrorKind: 'error' | 'forbidden' | null = null;
  notice = '';
  private identity = '';
  private sourceAvailable = false;
  private generation = 0;
  private readRequest?: Subscription;
  private writeRequest?: Subscription;
  private readonly reasons = new Map<string, string>();
  private intent: { fingerprint: string; key: string } | null = null;
  readonly standalone = { standalone: true };
  get reason(): string {
    return (
      this.reasons.get(
        `${this.workflowId}/${this.selected?.invocationId ?? ''}`,
      ) ?? ''
    );
  }
  set reason(value: string) {
    this.reasons.set(
      `${this.workflowId}/${this.selected?.invocationId ?? ''}`,
      value,
    );
    this.pendingInputChange.emit(
      [...this.reasons.values()].some((reason) => !!reason.trim()),
    );
  }
  ngOnChanges(): void {
    const key = `${this.workflowId}/${this.occurrenceId}`;
    if (key !== this.identity) {
      this.identity = key;
      this.generation++;
      this.readRequest?.unsubscribe();
      this.loading = false;
      this.sourceAvailable = false;
      this.data = null;
      this.rows = [];
      this.selected = null;
      this.resultFields = [];
      this.stale = false;
      this.error = '';
      this.readErrorKind = null;
    }
    if (this.autoLoad && !this.data && !this.loading && !this.readErrorKind)
      this.refresh();
  }
  ngOnDestroy(): void {
    this.generation++;
    this.readRequest?.unsubscribe();
    this.writeRequest?.unsubscribe();
    this.busyChange.emit(false);
    this.pendingInputChange.emit(false);
  }
  refresh(): void {
    if (
      !Number.isSafeInteger(this.workflowId) ||
      this.workflowId < 1 ||
      this.busy ||
      this.interactionBlocked
    )
      return;
    const generation = ++this.generation,
      workflowId = this.workflowId;
    this.readRequest?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.readErrorKind = null;
    this.readRequest = this.api.read(workflowId).subscribe({
      next: (data) => {
        if (generation !== this.generation || workflowId !== this.workflowId)
          return;
        this.loading = false;
        if (!validRead(data, workflowId)) {
          this.readErrorKind = 'error';
          this.error =
            'Automated execution returned an incompatible source. Refresh before recovery.';
          this.stale = true;
          this.sourceAvailable = false;
          return;
        }
        const previousId = this.selected?.invocationId;
        this.data = data;
        this.sourceAvailable = true;
        this.rows = data.runs.filter(
          (run) =>
            this.occurrenceId === null ||
            run.occurrenceId === this.occurrenceId,
        );
        this.select(
          previousId && this.rows.some((run) => run.invocationId === previousId)
            ? previousId
            : (this.rows.find((run) => run.isCurrent)?.invocationId ??
                this.rows[0]?.invocationId ??
                ''),
        );
      },
      error: (error) => {
        if (generation !== this.generation) return;
        this.loading = false;
        this.readErrorKind = [401, 403].includes(error?.status)
          ? 'forbidden'
          : 'error';
        this.error =
          this.readErrorKind === 'forbidden'
            ? 'You do not have permission to view automated execution for this selection.'
            : 'Automated execution could not be read. Retry the read; entered reasons are kept.';
        this.stale = true;
        this.sourceAvailable = false;
        if ([401, 403].includes(error?.status)) {
          this.data = null;
          this.rows = [];
          this.selected = null;
          this.resultFields = [];
        }
      },
    });
  }
  select(invocationId: string): void {
    if (this.busy || this.interactionBlocked) return;
    this.selected =
      this.rows.find((run) => run.invocationId === invocationId) ?? null;
    this.stale = !this.sourceAvailable;
    const result = this.selected?.result;
    this.resultFields =
      result && typeof result === 'object' && !Array.isArray(result)
        ? Object.entries(result)
            .filter(
              ([, value]) =>
                value === null ||
                ['string', 'boolean'].includes(typeof value) ||
                (typeof value === 'number' && Number.isFinite(value)),
            )
            .map(([name, value]) => ({ name, value }))
        : [];
  }
  control(action: 'cancel' | 'restart'): void {
    const run = this.selected,
      reason = this.reason.trim(),
      workflowId = this.workflowId;
    if (
      !run ||
      this.loading ||
      this.readErrorKind !== null ||
      this.busy ||
      this.interactionBlocked ||
      this.stale ||
      (action === 'cancel' ? !run.canCancel : !run.canRestart)
    )
      return;
    if (!reason || reason.length > 1000) {
      this.error = 'Supply a recovery reason of up to 1000 characters.';
      return;
    }
    const fingerprint = JSON.stringify([
      workflowId,
      run.invocationId,
      run.workflowETag,
      run.occurrenceId,
      action,
      reason,
    ]);
    if (this.intent?.fingerprint !== fingerprint)
      this.intent = { fingerprint, key: crypto.randomUUID() };
    this.generation++;
    this.readRequest?.unsubscribe();
    this.loading = false;
    this.busy = true;
    this.busyChange.emit(true);
    this.error = '';
    this.notice = '';
    this.writeRequest = this.api
      .control(workflowId, run, action, reason, this.intent.key)
      .subscribe({
        next: (receipt) => {
          this.busy = false;
          this.busyChange.emit(false);
          if (workflowId !== this.workflowId) return;
          if (
            !validRead(receipt, workflowId) ||
            (action === 'cancel'
              ? !receipt.runs.some(
                  (item) =>
                    item.invocationId === run.invocationId &&
                    item.status === 'cancelled',
                )
              : !receipt.runs.some(
                  (item) =>
                    item.invocationId !== run.invocationId &&
                    item.supersedesInvocationId === run.invocationId,
                ))
          ) {
            this.error =
              'The recovery receipt did not match the requested execution. Your reason is kept; refresh before another request.';
            this.stale = true;
            return;
          }
          this.reason = '';
          this.sourceAvailable = true;
          this.data = receipt;
          this.rows = receipt.runs.filter(
            (item) =>
              this.occurrenceId === null ||
              item.occurrenceId === this.occurrenceId,
          );
          this.select(
            action === 'restart'
              ? (this.rows.find(
                  (item) => item.supersedesInvocationId === run.invocationId,
                )?.invocationId ?? run.invocationId)
              : run.invocationId,
          );
          this.intent = null;
          this.notice =
            action === 'cancel'
              ? 'Cancellation recorded. Refreshing the retained execution state.'
              : 'Replacement requested as new work. Refreshing the server-selected invocation.';
          this.changed.emit();
          this.refresh();
        },
        error: (error) => {
          this.busy = false;
          this.busyChange.emit(false);
          if (workflowId !== this.workflowId) return;
          this.error =
            error?.error?.detail ??
            'The recovery request failed. Your reason is kept.';
          this.stale = error?.status === 412;
          if (this.stale) this.sourceAvailable = false;
          if ([401, 403].includes(error?.status)) {
            this.data = null;
            this.rows = [];
            this.selected = null;
            this.resultFields = [];
          }
        },
      });
  }
  statusLabel(status: string): string {
    return (
      (
        {
          pending: 'Pending system execution',
          running: 'Running',
          retry: 'Technical retry pending',
          retry_pending: 'Technical retry pending',
          failed: 'Technical failure · required work blocked',
          exhausted: 'Retries exhausted · required work blocked',
          completed: 'Completed',
          cancelled: 'Cancelled',
          superseded: 'Superseded',
        } as Record<string, string>
      )[status] ?? status.replace(/_/g, ' ')
    );
  }
}

function validRead(
  data: WorkflowAutomatedActivitiesRead,
  workflowId: number,
): boolean {
  return (
    !!data &&
    data.contractVersion === 1 &&
    data.workflowInstanceId === workflowId &&
    typeof data.workerConfigured === 'boolean' &&
    typeof data.dispatchEnabled === 'boolean' &&
    Array.isArray(data.runs) &&
    data.runs.length <= 100 &&
    data.runs.every(
      (run) =>
        !!run.invocationId &&
        !!run.command?.key &&
        Number.isSafeInteger(run.occurrenceId) &&
        run.occurrenceId > 0 &&
        Number.isSafeInteger(run.attempts) &&
        run.attempts >= 0 &&
        typeof run.canCancel === 'boolean' &&
        typeof run.canRestart === 'boolean' &&
        !!run.workflowETag &&
        !!run.occurrenceETag,
    )
  );
}
